import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { formatPriceDisplay } from '@/util/formatting';
import { getServiceUnitPrice } from '@/util/service-pricing';
import type { ServiceType } from '@/components/ui/carousel';
import { useServicePillOverflow } from './useServicePillOverflow';

type Props = {
  services: ServiceType[];
  disabled: boolean;
  onPickService: (service: ServiceType) => void;
};

const priceLabel = (s: ServiceType) => {
  const unit = getServiceUnitPrice(s);
  return unit === 0 ? 'Free' : `$${formatPriceDisplay(unit)}`;
};

/**
 * Catalog display order for asset services: `onlineSortOrder` ascending, with
 * unordered services parked at the end. Exported because the sub-lg services
 * bottom sheet (AssetServicesSheet) is the same catalog in a different shell
 * and must present it in the same order as these pills.
 */
export const sortAssetServices = (services: ServiceType[]) =>
  [...services].sort(
    (a, b) =>
      (a.onlineSortOrder ?? Number.MAX_SAFE_INTEGER) -
      (b.onlineSortOrder ?? Number.MAX_SAFE_INTEGER),
  );

export default function AssetServicePills({ services, disabled, onPickService }: Props) {
  // Memoize so the hook receives a stable array reference while `services` is
  // unchanged — a fresh array each render would thrash the overflow effect.
  const ordered = useMemo(() => sortAssetServices(services), [services]);
  const { containerRef, registerWidth, visible, overflow } = useServicePillOverflow(ordered);

  // Outer (measured) box: flex-1 so the row fills the available width and its
  // measured clientWidth GROWS back when the window widens — without it the box
  // only ever shrinks (flex-grow 0), so pills moved into "More" never return.
  // It is NOT `overflow-hidden`: only the inner pills row clips, so the More
  // trigger (a sibling) can never be clipped off the right edge.
  //
  // Deliberately NO min-w-0 here: the default `min-width: auto` floors this box
  // at its min-content, so the search field and photoset select shrink before
  // it does. With min-w-0 the box shrank to 0 at narrow desktop widths and the
  // shrink-0 trigger spilled out under the search field, unclickable (DV-3193
  // QA, 1024–1100px).
  //
  // That floor includes the VISIBLE pills, not just the trigger. It can't pin
  // pills in place because the split reserves MORE_BUTTON_WIDTH for the
  // trigger, which is wider than the rendered More button: a box squeezed to
  // its floor always measures too narrow for its current pills, so they move
  // into the menu and the floor drops to the trigger alone. Keep that reserve
  // above the real trigger width (useServicePillOverflow.ts).
  //
  // Inner (clip) box: sized to its content — NOT flex-1, which would stretch it
  // and shove the More trigger to the row's far right edge instead of letting
  // it sit next to the last pill. min-w-0 keeps it shrinkable, so if the split
  // is momentarily stale (mid-resize) the pills clip instead of pushing More.
  //
  // This bar only renders as AssetsControlBar's desktop-only trailingActions
  // slot (Task 5) — sub-lg reaches services via the selection bar's Services
  // sheet instead — so there is no sub-lg row to size this container for.
  return (
    <div
      ref={containerRef}
      className="flex flex-1 items-center gap-2"
    >
      <div
        data-testid="service-pills-clip"
        className="flex min-w-0 items-center gap-2 overflow-hidden"
      >
        {visible.map((s) => (
          <button
            key={s.uuid}
            type="button"
            disabled={disabled}
            ref={(el) => el && registerWidth(s.uuid, el.offsetWidth)}
            onClick={() => onPickService(s)}
            // max-lg:min-h-11 — 44px tap-target floor below lg, matching the
            // Button primitive's mobile floor (ui/button.tsx). Height only;
            // the overflow math measures offsetWidth, which is unchanged.
            className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full border border-input px-3 py-1.5 max-lg:min-h-11 text-sm font-medium text-foreground transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-input disabled:hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            {s.name}
            <span className="text-muted-foreground">{priceLabel(s)}</span>
          </button>
        ))}
      </div>

      {overflow.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={disabled} className="shrink-0 rounded-full border-input">
              {/* With no pill beside it there is nothing for "More" to be more
                  than — name the menu by its contents instead. */}
              {visible.length === 0 ? 'Services' : 'More'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {overflow.map((s) => (
              <DropdownMenuItem key={s.uuid} onSelect={() => onPickService(s)} className="flex justify-between gap-6">
                <span>{s.name}</span>
                <span className="text-muted-foreground">{priceLabel(s)}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
